import { useEffect, useState } from "react";
import api from "../lib/api";
import { useLang } from "../context/LanguageContext";
import { resolveImageUrl } from "../lib/url";
import { Calendar, MapPin, Sparkles } from "lucide-react";
import Seo from "../components/Seo";

const Events = () => {
  const { t } = useLang();
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/public/events").then(r => setItems(r.data)); }, []);

  const eventsJsonLd = items.length > 0 ? {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "itemListElement": items.map((e, i) => ({
      "@type": "ListItem",
      "position": i + 1,
      "item": {
        "@type": "Event",
        "name": e.title,
        "description": e.description,
        "startDate": e.date,
        "image": e.image,
        "location": {
          "@type": "Place",
          "name": e.location,
          "address": e.location,
        },
        "organizer": { "@type": "Organization", "name": "KK Academy" },
        "eventStatus": e.status === "planned"
          ? "https://schema.org/EventScheduled"
          : "https://schema.org/EventScheduled",
        "eventAttendanceMode": "https://schema.org/OfflineEventAttendanceMode",
      },
    })),
  } : null;

  return (
    <div data-testid="events-page">
      <Seo
        title="Tamilische Kultur-Events & KAAVIYAM"
        description="Entdecke die kommenden tamilischen Kultur-Events der KK Academy – KAAVIYAM Jahreskonzert, Talent Shows und mehr in der Schweiz."
        path="/events"
        jsonLd={eventsJsonLd}
        keywords="Kaaviyam, Tamilische Festivitäten, Tamilische Kultur Events, Eventmanagement, Tamilische Tanzperformance"
      />
      <section className="py-20 max-w-7xl mx-auto px-6">
        <div className="text-xs uppercase tracking-[0.3em] text-primary mb-4">{t("nav.events")}</div>
        <h1 className="font-serif-display text-5xl md:text-6xl leading-[1.05] max-w-3xl mb-12">{t("home.eventsTitle")}</h1>

        <div className="space-y-12">
          {items.map((e) => (
            e.is_signature ? (
              // ===== Signature / Hero Event (KAAVIYAM) =====
              <article key={e.id} data-testid={`event-${e.id}`} className="card-academy rounded-2xl overflow-hidden">
                <div className="relative bg-[hsl(150_28%_10%)] py-14 px-6 sm:px-10 text-white">
                  <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-[hsl(var(--brand-gold))/0.5] text-[hsl(var(--brand-gold-soft))] text-[11px] uppercase tracking-[0.3em] mb-8">
                    <Sparkles className="w-3.5 h-3.5" /> Signature Event
                  </div>
                  {/* Both logos */}
                  {e.extra_logos && e.extra_logos.length > 0 && (
                    <div className="flex flex-col sm:flex-row items-center justify-center gap-8 sm:gap-16 mb-10">
                      {e.extra_logos.map((l, i) => (
                        <img key={i} src={resolveImageUrl(l)} alt={`KAAVIYAM ${i === 0 ? "Tamil" : "English"}`} className="h-40 sm:h-48 w-auto object-contain" />
                      ))}
                    </div>
                  )}
                  <div className="text-center max-w-3xl mx-auto">
                    <div className="text-xs uppercase tracking-[0.3em] text-[hsl(var(--brand-gold))] mb-2">{e.date} · {e.time}</div>
                    <h2 className="font-serif-display text-4xl sm:text-5xl mb-4 text-white">{e.title}</h2>
                    <p className="text-white/80 leading-relaxed">{e.description}</p>
                    <div className="mt-6 inline-flex items-center gap-2 text-sm text-white/70"><MapPin className="w-4 h-4 text-[hsl(var(--brand-gold))]" />{e.location}</div>
                  </div>
                </div>
              </article>
            ) : (
              // ===== Standard Event =====
              <div key={e.id} data-testid={`event-${e.id}`} className="grid md:grid-cols-2 gap-8 card-academy rounded-lg overflow-hidden">
                <div className="aspect-video md:aspect-auto overflow-hidden">
                  <img src={resolveImageUrl(e.image)} alt={e.title} className="w-full h-full object-cover" />
                </div>
                <div className="p-8 flex flex-col justify-center">
                  <div className="text-xs uppercase tracking-[0.3em] text-primary mb-3">{(e.type || "").replace("_", " ")}</div>
                  <h2 className="font-serif-display text-3xl mb-4">{e.title}</h2>
                  <p className="text-muted-foreground mb-5">{e.description}</p>
                  <div className="space-y-2 text-sm text-muted-foreground">
                    <div className="flex items-center gap-2"><Calendar className="w-4 h-4 text-primary" />{e.date} · {e.time}</div>
                    <div className="flex items-center gap-2"><MapPin className="w-4 h-4 text-primary" />{e.location}</div>
                  </div>
                </div>
              </div>
            )
          ))}
        </div>
      </section>
    </div>
  );
};

export default Events;
